import React, { useState, useEffect, useRef } from 'react';
import './InstallBanner.css';
import { useAuth } from '../auth/useAuth';

export function isIOS() {
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) &&
    !window.navigator.standalone
  );
}

export function isStandalone() {
  return (
    window.navigator.standalone === true ||
    (typeof window.matchMedia === 'function' &&
      !!window.matchMedia('(display-mode: standalone)').matches)
  );
}

export default function InstallBanner() {
  const { user } = useAuth();

  // useRef damit der manuelle Trigger-Handler immer den aktuellen Prompt sieht
  const deferredPromptRef = useRef(null);
  const [deferredPrompt, setDeferredPromptState] = useState(null);

  const [showAndroidBanner, setShowAndroidBanner] = useState(false);
  const [showIOSBanner, setShowIOSBanner] = useState(false);

  function setDeferredPrompt(val) {
    deferredPromptRef.current = val;
    setDeferredPromptState(val);
  }

  useEffect(() => {
    if (isStandalone()) return;

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      // Banner nur automatisch zeigen wenn User eingeloggt und kein Cooldown
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      const inCooldown = dismissed && Date.now() - parseInt(dismissed, 10) < 24 * 60 * 60 * 1000;
      if (user && !inCooldown) setShowAndroidBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // iOS: automatisch zeigen wenn User eingeloggt und kein Cooldown
    if (isIOS() && user) {
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      const inCooldown = dismissed && Date.now() - parseInt(dismissed, 10) < 24 * 60 * 60 * 1000;
      if (!inCooldown) setShowIOSBanner(true);
    }

    // Manueller Trigger aus den Benutzer-Einstellungen
    const handleManualTrigger = () => {
      if (isStandalone()) return;
      localStorage.removeItem('pwa-install-dismissed');
      if (deferredPromptRef.current) {
        setShowAndroidBanner(true);
      } else if (isIOS()) {
        setShowIOSBanner(true);
      }
    };
    window.addEventListener('pwaInstallTrigger', handleManualTrigger);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('pwaInstallTrigger', handleManualTrigger);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function handleInstallClick() {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    console.log('[PWA] Install outcome:', outcome);
    setDeferredPrompt(null);
    setShowAndroidBanner(false);
  }

  function handleDismiss() {
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    setShowAndroidBanner(false);
    setShowIOSBanner(false);
  }

  // Install-Banner nur für eingeloggte Benutzer.
  // Updates gehören NICHT hierher — dafür ist UpdateBanner.js die einzige Quelle.
  if (!user) return null;

  // Android: beforeinstallprompt verfügbar
  if (showAndroidBanner) {
    return (
      <div className="install-banner" role="banner">
        <div className="install-banner__content">
          <img src="/logo192.png" alt="techsoul" className="install-banner__logo" />
          <div className="install-banner__text">
            <strong>App installieren</strong>
            <span>Direkt auf dem Homescreen – wie eine native App.</span>
          </div>
        </div>
        <div className="install-banner__actions">
          <button
            className="install-banner__btn install-banner__btn--primary"
            onClick={handleInstallClick}
          >
            Installieren
          </button>
          <button
            className="install-banner__btn install-banner__btn--ghost"
            onClick={handleDismiss}
          >
            Nicht jetzt
          </button>
        </div>
      </div>
    );
  }

  // iOS: manuelle Anleitung (Safari kennt kein beforeinstallprompt)
  if (showIOSBanner) {
    return (
      <div className="install-banner install-banner--ios" role="banner">
        <button
          className="install-banner__close"
          onClick={handleDismiss}
          aria-label="Schließen"
        >
          ✕
        </button>
        <div className="install-banner__content">
          <img src="/logo192.png" alt="techsoul" className="install-banner__logo" />
          <div className="install-banner__text">
            <strong>App installieren</strong>
            <span>
              Tippe auf{' '}
              <span className="install-banner__share-icon" aria-label="Teilen-Symbol">
                <svg width="14" height="17" viewBox="0 0 14 17" fill="currentColor" aria-hidden="true" style={{ display: 'inline', verticalAlign: 'middle', marginBottom: '2px' }}>
                  <path d="M7 0L3.5 3.5h2V10h3V3.5h2L7 0z"/>
                  <path d="M11.5 6v9h-9V6H1v10.5h12V6h-1.5z"/>
                </svg>
              </span>{' '}
              und dann auf <em>„Zum Home-Bildschirm"</em>
            </span>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
